/**
 * Trägt die Notiz einer Rate eine gespeicherte Bankreferenz (Marker plus referenzartiger Wert: mindestens
 * zwölf Zeichen ohne Leerraum, mit Ziffer)? Der Bankabgleich schreibt beim Bestätigen
 * „Externe Zahlung erfasst: <acctSvcrRef>“ bzw. „…, Bankreferenz: <acctSvcrRef>“ an die Rate; dieselbe Zeile
 * entsteht bei „Außerhalb Mollie bezahlt erfassen“ mit Referenz. Freitext wie „Bankreferenz folgt“,
 * „Externe Zahlung erfasst: bar“ oder ein Datum zählt nicht. Gemeinsam für Matcher (Server) und Ratenlisten (Client).
 */
// Vom Bankabgleich geschriebene Zeilen: „07.10.2026 09:00 Externe Zahlung erfasst (Bankabgleich): <ref>“ (Bestätigen und
// Sammelaktion seit #1388) und „07.10.2026 09:00 Rücklastschrift MS03 aus Bankabgleich, Bankreferenz: <ref>“. Die Referenz
// ist der komplette Rest der Zeile, die Bank liefert sie auch mit Leerzeichen. Für bestehende Rücklastschrift-Notizen mit
// kurzer Referenz ändert sich das Ergebnis, deshalb MATCHER_VERSION 2026-10-09.16 (Review #1388).
const GENERATED_NOTE = /^\d{2}\.\d{2}\.\d{4} \d{2}:\d{2} (?:Externe Zahlung erfasst \(Bankabgleich\)|Rücklastschrift(?: \S+)? aus Bankabgleich, Bankreferenz): \S.*$/u;

// Eine Rate kann zwei Referenzen tragen: die der Rücklastschrift und die der Nachzahlung (Pilot RE-43063, RE-47460).
// Rückgabe sind nur die Zeile des Bankabgleichs und das Altformat „Retoure RE-…, Bankreferenz …“; jede andere Zeile,
// auch eine Handnotiz mit „Rücklastschrift“, zählt als Zahlung. Das ist der sichere Rückfall: Eine Zahlungsreferenz
// schließt die Rate wie vor #1421 aus U1 aus (Review #1421).
// „non-payment“: jede Referenzzeile, die nicht eindeutig eine Zahlung ist (Rückgabe, Handnotiz, unbekanntes Format). U2 nimmt
// das für wieder bezahlte Raten: Nur die erkannte Zahlungszeile darf dort übergangen werden (Review #1454).
export type BankReferenceKind = "any" | "payment" | "return" | "non-payment";

const PAYMENT_LINE = /externe zahlung erfasst|external payment recorded/iu;
const RETURN_LINE = /^(?:\d{2}\.\d{2}\.\d{4} \d{2}:\d{2} Rücklastschrift(?: \S+)? aus Bankabgleich, Bankreferenz: \S|Retoure RE-?\s?\d+, Bankreferenz:? \S)/iu;

const referenceKindOf = (line: string): BankReferenceKind => (RETURN_LINE.test(line.trim()) ? "return" : "payment");

export const noteHasBankReference = (note: string | null | undefined, kind: BankReferenceKind = "any"): boolean =>
    (note ?? "").split(/\r?\n/).some((line) => {
        if (kind === "non-payment" ? PAYMENT_LINE.test(line) : kind !== "any" && referenceKindOf(line) !== kind) {
            return false;
        }
        const marker = /(?:bankreferenz|bank reference|externe zahlung erfasst(?: \(bankabgleich\))?|external payment recorded)\s*:?/i.exec(line);

        if (!marker) {
            return false;
        }
        // Genau die vom Bankabgleich geschriebene Zeile (Zeitstempel, Aktion, Referenz) trägt die Referenz der Bank, egal
        // wie kurz sie ist; Freitext mit denselben Wörtern zählt nicht (Review #1388).
        if (GENERATED_NOTE.test(line.trim())) {
            return true;
        }
        const value = line.slice(marker.index + marker[0].length).trim().split(/\s+/)[0] ?? "";

        // Mindestens zwölf Zeichen: Datum, Betrag und Kurztext fallen heraus, echte Referenzen (Pilot: 19 Ziffern) nicht.
        return value.length >= 12 && /\d/.test(value);
    });
